<template>
  <div v-if="visible" class="ai-setup-summary" :class="{ 'has-attention': needsAttention.length > 0 }">
    <button type="button" class="ai-setup-dismiss" title="Dismiss" @click="dismissed = true">
      <i class="fa fa-times"></i>
    </button>

    <div class="ai-setup-head">
      <i class="fa fa-magic"></i>
      <span>AI set up this form — review before saving</span>
    </div>

    <div v-if="configured.length" class="ai-setup-group">
      <div class="ai-setup-group-title done"><i class="fa fa-check-circle"></i> Set up for you</div>
      <ul>
        <li v-for="(c, i) in configured" :key="'cfg' + i">
          <span class="ai-setup-label">{{ c.label }}:</span> <span class="ai-setup-value">{{ c.value }}</span>
        </li>
      </ul>
    </div>

    <div v-if="needsAttention.length" class="ai-setup-group">
      <div class="ai-setup-group-title attention"><i class="fa fa-exclamation-triangle"></i> Needs your attention before saving</div>
      <ul>
        <li v-for="(n, i) in needsAttention" :key="'att' + i" class="attention-item">
          <span v-if="n.label" class="ai-setup-label">{{ n.label }}:</span> {{ n.message }}
        </li>
      </ul>
    </div>

    <div v-if="notes.length" class="ai-setup-group">
      <div class="ai-setup-group-title note"><i class="fa fa-info-circle"></i> Notes</div>
      <ul>
        <li v-for="(t, i) in notes" :key="'note' + i" class="note-item">{{ t }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
/**
 * AiSetupSummary — persistent, dismissible panel shown in the builder after an AI
 * "Create with AI" draft or an "Edit with AI" apply. Tells the staff member exactly
 * what the AI configured (categories, filename, form type) and — specifically — what
 * it could NOT do and they must fix before saving. Pure renderer: all wording comes
 * from the backend `setupReport`.
 */
export default {
  name: 'AiSetupSummary',
  props: {
    // { configured: [{ key, label, value }], needsAttention: [{ key, label, message }] }
    report: { type: Object, default: null },
    // Optional free-text notes (e.g. generation warnings / auto-adjustments).
    notes: { type: Array, default: () => [] },
  },
  data() {
    return { dismissed: false };
  },
  computed: {
    configured() {
      return (this.report && Array.isArray(this.report.configured)) ? this.report.configured : [];
    },
    needsAttention() {
      return (this.report && Array.isArray(this.report.needsAttention)) ? this.report.needsAttention : [];
    },
    visible() {
      if (this.dismissed) return false;
      return this.configured.length > 0 || this.needsAttention.length > 0 || this.notes.length > 0;
    },
  },
  watch: {
    // A fresh report (e.g. after another AI edit) should re-show even if dismissed.
    report() { this.dismissed = false; },
  },
};
</script>

<style lang="scss" scoped>
.ai-setup-summary {
  position: relative;
  background: #f0fdf9;
  border: 1px solid #a7f3d0;
  border-left: 4px solid #3abf94;
  border-radius: 10px;
  padding: 14px 40px 12px 16px;
  margin-bottom: 20px;

  &.has-attention {
    background: #fffbeb;
    border-color: #fde68a;
    border-left-color: #f59e0b;
  }
}

.ai-setup-dismiss {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: #94a3b8;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover { background: rgba(0, 0, 0, 0.06); color: #475569; }
}

.ai-setup-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  color: #0f172a;
  margin-bottom: 10px;

  .fa { color: #6366f1; }
}

.ai-setup-group {
  margin-bottom: 8px;

  &:last-child { margin-bottom: 0; }

  ul {
    margin: 4px 0 0;
    padding-left: 24px;
  }

  li {
    font-size: 0.85rem;
    color: #334155;
    margin-bottom: 3px;
    line-height: 1.4;
  }

  li.attention-item { color: #92400e; }
  li.note-item { color: #64748b; }
}

.ai-setup-group-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &.done { color: #15803d; }
  &.attention { color: #b45309; }
  &.note { color: #64748b; }
}

.ai-setup-label { font-weight: 600; }
.ai-setup-value { color: #0f172a; }

@media (max-width: 768px) {
  .ai-setup-summary { padding-right: 16px; }
}
</style>
